import { useState } from 'react';
import { Mail, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface EmailCellProps {
  value: string | null;
  onChange: (value: string | null) => void;
  readOnly?: boolean;
}

export function EmailCell({ value, onChange, readOnly }: EmailCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || '');

  const isValidEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSave = () => {
    const trimmed = tempValue.trim();
    if (!trimmed) {
      onChange(null);
      setIsEditing(false);
      return;
    }

    if (isValidEmail(trimmed)) {
      onChange(trimmed);
      setIsEditing(false);
    }
  };

  const handleClear = () => {
    setTempValue('');
    onChange(null);
    setIsEditing(false);
  };

  const isInvalid = tempValue.trim() && !isValidEmail(tempValue.trim());

  if (readOnly && !value) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  if (readOnly && value) {
    return (
      <a
        href={`mailto:${value}`}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
      >
        <Mail className="w-4 h-4" />
        <span className="truncate">{value}</span>
      </a>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[300px]">
        <Input
          type="email"
          placeholder="email@exemplo.com"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isInvalid) handleSave();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          className={isInvalid ? 'border-red-500' : ''}
          autoFocus
        />
        {isInvalid && (
          <p className="text-xs text-red-500">Email inválido</p>
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isInvalid}>
            Salvar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
            Cancelar
          </Button>
          {value && (
            <Button size="sm" variant="ghost" onClick={handleClear}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <div
        className="text-gray-400 text-sm cursor-pointer hover:text-gray-600"
        onClick={() => setIsEditing(true)}
      >
        Adicionar email
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      <a
        href={`mailto:${value}`}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <Mail className="w-4 h-4" />
        <span className="truncate">{value}</span>
      </a>
    </div>
  );
}
